import React, { useState } from 'react';
import { ALL_PRODUCTS, PRODUCT_CATEGORIES } from '../data/constants';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { scrollToSection } from '../utils/scrollTo';
import ProductModal from './ProductModal';
import type { Product } from '../types';

const Products: React.FC = () => {
  const { setRef, isVisible } = useIntersectionObserver();
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [active, setActive] = useState<Product | null>(null);

  const filtered = activeCategory === 'All'
    ? ALL_PRODUCTS
    : ALL_PRODUCTS.filter(p => p.tag === activeCategory);

  return (
    <section id="products" ref={setRef('products')} style={{ background: '#fff', padding: '96px 0 80px' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>

        {/* ── Header ── */}
        <div className={`sf ${isVisible('products') ? 'vis' : ''}`} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 40, flexWrap: 'wrap', gap: 20 }}>
          <div>
            <div className="eyebrow-dark">Our Products</div>
            <h2 className="section-h2" style={{ marginBottom: 0 }}>Complete GRP Solutions Range</h2>
          </div>
          <p style={{ fontSize: 15, color: '#718096', maxWidth: 380, lineHeight: 1.7 }}>
            28+ product categories engineered for industrial, architectural, and urban applications across the Gulf.
          </p>
        </div>

        {/* ── Category Tabs ── */}
        <div className={`sf ${isVisible('products') ? 'vis' : ''}`} style={{ transitionDelay: '0.1s', display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 40, paddingBottom: 20, borderBottom: '1px solid #e2e8f0' }}>
          {PRODUCT_CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              style={{
                padding: '9px 20px', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', border: '1.5px solid',
                borderRadius: 2, cursor: 'pointer', transition: 'all 0.25s', fontFamily: "'DM Sans', sans-serif",
                background: activeCategory === cat ? '#f5c518' : 'white',
                borderColor: activeCategory === cat ? '#f5c518' : '#e2e8f0',
                color: activeCategory === cat ? '#1a2b4a' : '#718096',
                boxShadow: activeCategory === cat ? '0 4px 12px rgba(245,197,24,0.3)' : 'none',
              }}
            >
              {cat}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 13, color: '#a0aec0', fontWeight: 500, alignSelf: 'center' }}>
            {filtered.length} product{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* ── Product Grid ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
          {filtered.map((product, i) => (
            <div
              key={product.title}
              className={`prod-card sf ${isVisible('products') ? 'vis' : ''}`}
              style={{ transitionDelay: `${Math.min(i * 0.05, 0.4)}s`, cursor: 'pointer' }}
              onClick={() => setActive(product)}
            >
              {/* Image zone */}
              <div className="prod-img-wrap">
                <img src={product.img} alt={product.title} className="prod-img" />
                <div className="prod-img-overlay" />
                <span className="prod-tag">{product.tag}</span>
                <div className="prod-hover-cta">
                  <span>View Details →</span>
                </div>
              </div>

              {/* Info zone */}
              <div className="prod-info">
                <div className="prod-icon">{product.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 className="prod-title">{product.title}</h3>
                  <p className="prod-subtitle">{product.subtitle}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── CTA ── */}
        <div className={`sf ${isVisible('products') ? 'vis' : ''}`} style={{ textAlign: 'center', marginTop: 56, paddingTop: 40, borderTop: '1px solid #e2e8f0' }}>
          <p style={{ fontSize: 15, color: '#718096', marginBottom: 20 }}>
            Need a custom GRP solution or can't find what you're looking for?
          </p>
          <button className="btn-yellow" onClick={() => scrollToSection('contact')}>
            Enquire About Custom Fabrication →
          </button>
        </div>
      </div>

      {active && (
        <ProductModal
          product={active}
          onClose={() => setActive(null)}
          onQuote={() => { setActive(null); scrollToSection('contact'); }}
        />
      )}

      <style>{`
        .prod-card { background: white; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; transition: transform 0.35s cubic-bezier(0.23,1,0.32,1), box-shadow 0.35s ease, border-color 0.25s; }
        .prod-card:hover { transform: translateY(-7px); box-shadow: 0 20px 48px rgba(0,0,0,0.12); border-color: #f5c518; }
        .prod-img-wrap { position: relative; height: 220px; overflow: hidden; background: #e8eff5; }
        .prod-img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.55s ease; display: block; }
        .prod-card:hover .prod-img { transform: scale(1.07); }
        .prod-img-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(26,43,74,0.6) 0%, transparent 55%); }
        .prod-tag { position: absolute; top: 12px; right: 12px; background: rgba(255,255,255,0.95); color: #1a2b4a; font-size: 9px; font-weight: 800; letter-spacing: 0.16em; text-transform: uppercase; padding: 4px 10px; border-radius: 100px; }
        .prod-hover-cta { position: absolute; bottom: 14px; left: 14px; font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #f5c518; opacity: 0; transform: translateY(8px); transition: all 0.3s; }
        .prod-card:hover .prod-hover-cta { opacity: 1; transform: translateY(0); }
        .prod-info { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-top: 2px solid transparent; transition: border-color 0.25s, background 0.25s; }
        .prod-card:hover .prod-info { border-top-color: #f5c518; background: #fffbea; }
        .prod-icon { font-size: 22px; flex-shrink: 0; }
        .prod-title { font-size: 15px; font-weight: 700; color: #1a2b4a; line-height: 1.15; margin: 0 0 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .prod-subtitle { font-size: 10px; font-weight: 600; letter-spacing: 0.07em; color: #a0aec0; text-transform: uppercase; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      `}</style>
    </section>
  );
};

export default Products;
